import React from 'react';
import {
    Badge,
    Button,
    List,
    Spinner,
} from 'reactstrap';
import { faLink, faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, { ApiRoutes, AppNavPaths } from '../Common';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { Block, BlockBorder } from '../common/Block';
import {
    AppPageForm, FormLabel, toasty,
} from '../common/forms/FormElements';
import { SaveButton } from '../common/forms/SaveButton';
import { Modal } from '../common/Modal';
import { InputTypeIds } from '../formsAndSurveys/FormsAndSurveys';
import { ExecutionHistory } from './common/ExecutionHistory';
import {
    isUnsaved,
    newChargeQuestion,
    questionTypeLabel,
    showChargeQuestionEditor,
} from './ChargeQuestionEditor';

const toQuestionPayload = (question) => ({
    ...question,
    id: isUnsaved(question.id) ? 0 : question.id,
    options: (question.options ?? []).map((o) => ({ ...o, id: isUnsaved(o.id) ? 0 : o.id })),
});

const renderHistoryQuestions = (title, questions) => questions.length > 0 && (
    <li>
        <strong>{title}</strong>
        <ul>
            {questions.map((q, i) => (
                <li key={i}>
                    {q.label ?? q.text ?? q.name ?? q.id}
                </li>
            ))}
        </ul>
    </li>
);

class ManageChargeQuestion extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);

        this.formRef = React.createRef();

        this.state = {
            loading: false,
            saving: false,
            isValidated: false,
            chargeTypes: [],
            selectedChargeType: null,
            linkedQuestions: [],
            availableQuestions: [],
            originalQuestionIds: [],
            editedQuestionIds: [],
        };
    }

    componentDidMount() {
        Promise.all([
            this.loadChargeTypesForTenant(),
            this.loadAllQuestions(),
        ]);
    }

    loadChargeTypesForTenant = async () => {
        try {
            const raw = await httpClient.js(
                ApiRoutes.chargeType.nonSurcharges(),
            );

            const chargeTypes = (raw || []).map((ct) => ({
                value: ct.id,
                label:
                    ct.description ||
                    ct.baseConfig?.name ||
                    ct.payrollChargeTypeName ||
                    `Charge Type #${ct.id}`,
            }));

            this.setState({ chargeTypes });
        } catch (e) {
            console.error(e);
            toasty.error('Unable to load charge types.');
        }
    };

    loadAllQuestions = async () => {
        try {
            const availableQuestions = await httpClient.js(
                ApiRoutes.typeAheads.getAllQuestion(),
            );
            this.setState({ availableQuestions });
        } catch (e) {
            console.error(e);
            toasty.error('Unable to load questions.');
        }
    };

    onChargeTypeChanged = async (selectedChargeType) => {
        this.setState({
            selectedChargeType,
            linkedQuestions: [],
            originalQuestionIds: [],
            editedQuestionIds: [],
            isValidated: false,
            loading: !!selectedChargeType,
        });

        if (!selectedChargeType) return;

        try {
            const linkedQuestions = await httpClient.js(
                ApiRoutes.adminTool.chargeTypeQuestions(selectedChargeType.value),
            ) ?? [];

            this.setState({
                linkedQuestions,
                originalQuestionIds: linkedQuestions.map((q) => q.id),
                loading: false,
            });
        } catch (e) {
            console.error(e);
            toasty.error('Unable to load questions for charge type.');
            this.setState({ loading: false });
        }
    };

    nextOrder = () => Math.max(-1, ...this.state.linkedQuestions.map((q) => q.order ?? 0)) + 1;

    onRemoveQuestion = (questionId) => {
        this.setState((prev) => ({
            linkedQuestions: prev.linkedQuestions.filter((q) => q.id !== questionId),
            editedQuestionIds: prev.editedQuestionIds.filter((id) => id !== questionId),
        }));
    };

    onNewQuestion = () => showChargeQuestionEditor(newChargeQuestion(this.nextOrder()), this.onQuestionSaved);

    onEditQuestion = (question) => showChargeQuestionEditor(question, this.onQuestionSaved);

    onQuestionSaved = (question) => {
        this.setState((prev) => {
            const exists = prev.linkedQuestions.some((q) => q.id === question.id);

            return {
                linkedQuestions: exists
                    ? prev.linkedQuestions.map((q) => (q.id === question.id ? question : q))
                    : [...prev.linkedQuestions, question],
                editedQuestionIds: isUnsaved(question.id) || prev.editedQuestionIds.includes(question.id)
                    ? prev.editedQuestionIds
                    : [...prev.editedQuestionIds, question.id],
            };
        });
    };

    openAddModal = () => {
        const {
            availableQuestions,
            linkedQuestions,
        } = this.state;

        const linkedIds = new Set(linkedQuestions.map((q) => q.id));
        const candidateOptions = availableQuestions.filter(
            (q) => !linkedIds.has(q.value),
        );

        Modal.show({
            title: 'Add Existing Question',
            onSubmit: (values) => this.confirmAddQuestion(values),
            body: () => (
                <ValidatedSelect
                    id="selectedQuestionToAdd"
                    label="Question"
                    name="selectedQuestionToAdd"
                    options={candidateOptions}
                    placeholder="Select a question"
                    required
                    error="You must select a question to add"
                />
            ),
        });
    };

    confirmAddQuestion = ({ selectedQuestionToAdd }) => {
        const { linkedQuestions, availableQuestions } = this.state;

        if (!selectedQuestionToAdd) {
            toasty.error('Please select a question to add.');
            return;
        }

        const alreadyLinked = linkedQuestions.some((q) => q.id === selectedQuestionToAdd);
        if (alreadyLinked) {
            toasty.error('That question is already linked to this charge type.');
            return;
        }

        const selected = availableQuestions.find((x) => x.value === selectedQuestionToAdd);

        // Only the name is known until the link is saved, so the question can be edited after Submit.
        this.setState((prev) => ({
            linkedQuestions: [
                ...prev.linkedQuestions,
                {
                    id: selected.value,
                    name: selected.label,
                    order: this.nextOrder(),
                    pendingLink: true,
                },
            ],
        }));
    };

    onSubmit = async () => {
        const {
            selectedChargeType,
            linkedQuestions,
            originalQuestionIds,
            editedQuestionIds,
        } = this.state;

        if (!selectedChargeType) {
            this.setState({ isValidated: true });
            toasty.error('Please choose a charge type.');
            return;
        }

        const newSelectedIds = linkedQuestions
            .filter((q) => !isUnsaved(q.id))
            .map((q) => q.id);

        const addedQuestionIds = newSelectedIds.filter(
            (id) => !originalQuestionIds.includes(id),
        );
        const removedQuestionIds = originalQuestionIds.filter(
            (id) => !newSelectedIds.includes(id),
        );
        const newQuestions = linkedQuestions
            .filter((q) => isUnsaved(q.id))
            .map(toQuestionPayload);
        const updatedQuestions = linkedQuestions
            .filter((q) => editedQuestionIds.includes(q.id))
            .map(toQuestionPayload);

        this.setState({ saving: true });

        try {
            await httpClient.post(
                ApiRoutes.adminTool.ManageChargeQuestion(selectedChargeType.value),
                {
                    chargeTypeId: selectedChargeType.value,
                    addedQuestionIds,
                    removedQuestionIds,
                    newQuestions,
                    updatedQuestions,
                },
            );

            toasty.success('Charge type questions saved.');

            await Promise.all([
                this.onChargeTypeChanged(selectedChargeType),
                this.loadAllQuestions(),
            ]);
        } catch (e) {
            console.error(e);
            toasty.error(e?.handledError?.message ?? 'Could not save questions – see console for details.');
        } finally {
            this.setState({ saving: false });
            if (this.props.refreshUsageHistory) {
                await this.props.refreshUsageHistory();
            }
        }
    };

    renderQuestionStatus(question) {
        if (isUnsaved(question.id)) {
            return (
                <Badge
                    color="success"
                    className="ml-2"
                >
                    New
                </Badge>
            );
        }
        if (question.pendingLink) {
            return (
                <Badge
                    color="info"
                    className="ml-2"
                >
                    Added
                </Badge>
            );
        }
        if (this.state.editedQuestionIds.includes(question.id)) {
            return (
                <Badge
                    color="warning"
                    className="ml-2"
                >
                    Edited
                </Badge>
            );
        }
        return null;
    }

    renderQuestionsTable() {
        const { linkedQuestions } = this.state;

        if (linkedQuestions.length === 0) {
            return (
                <small className="text-muted">
                    No questions linked – this charge type will not prompt for notes.
                </small>
            );
        }

        const sortedQuestions = [...linkedQuestions].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

        return (
            <table className="table table-sm mt-3">
                <thead>
                    <tr>
                        <th>Order</th>
                        <th>Question</th>
                        <th>Type</th>
                        <th>Required</th>
                        <th>Options</th>
                        <th style={{ width: '1px' }} />
                    </tr>
                </thead>
                <tbody>
                    {sortedQuestions.map((q) => (
                        <tr key={q.id}>
                            <td>{q.order}</td>
                            <td>
                                {q.name}
                                {this.renderQuestionStatus(q)}
                            </td>
                            <td>{q.pendingLink ? '' : questionTypeLabel(q)}</td>
                            <td>{!q.pendingLink && (q.answerRequired ? 'Yes' : 'No')}</td>
                            <td>
                                {q.inputTypeId === InputTypeIds.Select
                                    ? (q.options ?? []).filter((o) => o.isActive).map((o) => o.choiceText).join(', ')
                                    : ''}
                            </td>
                            <td className="text-right text-nowrap">
                                <Button
                                    color="link"
                                    size="sm"
                                    disabled={q.pendingLink}
                                    title={q.pendingLink ? 'Submit first to edit this question' : undefined}
                                    onClick={() => this.onEditQuestion(q)}
                                >
                                    Edit
                                </Button>
                                <Button
                                    color="link"
                                    size="sm"
                                    onClick={() => this.onRemoveQuestion(q.id)}
                                >
                                    Remove
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    }

    render() {
        const {
            loading,
            saving,
            isValidated,
            chargeTypes,
            selectedChargeType,
        } = this.state;

        if (!this.context?.user) return null;

        return (
            <AppPageForm
                backUrl={AppNavPaths.AdminTool}
                backLabel="Back to Admin Tools"
                formShown={this.context.formIsOpen}
                formId="manageChargeQuestionsForm"
                formName="manageChargeQuestionsForm"
                formHeading="Manage Charge Type Questions"
                formRef={this.formRef}
                formIsColumn
                saving={saving}
                isValidated={isValidated}
                onSubmit={this.onSubmit}
                setIsValidated={(value) => this.setState({ isValidated: value })}
            >
                <Block padding="medium">
                    <div>
                        <ValidatedSelect
                            id="chargeType"
                            name="chargeType"
                            label="Charge Type"
                            required
                            options={chargeTypes}
                            value={selectedChargeType}
                            onChange={this.onChargeTypeChanged}
                            placeholder="Select a charge type"
                            error="Select a charge type"
                            invalid={isValidated && !selectedChargeType}
                        />

                        {loading && (
                            <div className="d-flex justify-content-center my-2">
                                <Spinner size="sm" />
                                <span className="ml-2 text-muted">Loading questions … </span>
                            </div>
                        )}

                        {selectedChargeType && !loading && (
                            <>
                                <div className="d-flex flex-row flex-nowrap align-items-baseline justify-content-start mt-4">
                                    <FormLabel
                                        className="control-label"
                                        label="Linked Questions"
                                    />

                                    <Button
                                        color="outline-primary"
                                        size="sm"
                                        className="ml-auto"
                                        onClick={this.onNewQuestion}
                                    >
                                        <FontAwesomeIcon
                                            icon={faPlusCircle}
                                            className="mr-2"
                                        />
                                        New Question
                                    </Button>

                                    <Button
                                        color="outline-primary"
                                        size="sm"
                                        className="ml-2"
                                        onClick={this.openAddModal}
                                    >
                                        <FontAwesomeIcon
                                            icon={faLink}
                                            className="mr-2"
                                        />
                                        Add Existing
                                    </Button>
                                </div>

                                {this.renderQuestionsTable()}
                            </>
                        )}

                        <BlockBorder />

                        <div className="d-flex justify-content-center">
                            <SaveButton
                                saving={saving}
                                label="Submit"
                            />
                        </div>
                    </div>
                </Block>

                <ExecutionHistory usageHistory={this.props.usageHistory}>
                    {(record) => {
                        if (!record?.output) return null;

                        const { output } = record;

                        const chargeTypeName =
                            output.chargeTypeName ??
                            output.chargeType ??
                            output.chargeTypeLabel;

                        return (
                            <List type="unstyled">
                                {chargeTypeName && (
                                    <li>
                                        <strong>Charge Type:</strong>
                                        {' '}
                                        {chargeTypeName}
                                    </li>
                                )}

                                {renderHistoryQuestions('Created Questions:', output.createdQuestions ?? [])}
                                {renderHistoryQuestions('Updated Questions:', output.updatedQuestions ?? [])}
                                {renderHistoryQuestions('Added Questions:', output.addedQuestions ?? output.AddedQuestions ?? [])}
                                {renderHistoryQuestions('Removed Questions:', output.removedQuestions ?? output.RemovedQuestions ?? [])}
                            </List>
                        );
                    }}
                </ExecutionHistory>
            </AppPageForm>
        );
    }
}

export default ManageChargeQuestion;
